import { useState } from 'react'
import { Einblenden, Etikett, Wabennetz } from './Bausteine'
import { grusswort, grusswortLogo, moderation, speaker, speakerAbschnitt, type Speaker as SpeakerDaten } from '@/data/inhalte'

// Spitzes Sechseck, passend zum Seitenverhaeltnis 0,866 : 1 des Rahmens
const SECHSECK = 'polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)'

/** Silhouette, solange kein Foto vorliegt */
function Silhouette() {
  return (
    <svg aria-hidden="true" viewBox="0 0 200 230" className="absolute inset-x-0 bottom-0 h-[82%] w-full">
      <circle cx="100" cy="88" r="38" fill="rgba(255,255,255,.22)" />
      <path d="M26 230c0-58 32-94 74-94s74 36 74 94Z" fill="rgba(255,255,255,.22)" />
    </svg>
  )
}

/** Porträt in Wabenform mit feiner Kontur */
function Wabenportraet({ person, className = 'max-w-[230px]' }: Readonly<{ person: SpeakerDaten; className?: string }>) {
  return (
    <div className={`relative mx-auto aspect-[866/1000] w-full ${className}`}>
      <svg aria-hidden="true" viewBox="0 0 86.6 100" preserveAspectRatio="none" className="absolute -inset-2.5 h-[calc(100%+20px)] w-[calc(100%+20px)]">
        <polygon points="43.3,0.5 86.1,25.2 86.1,74.8 43.3,99.5 0.5,74.8 0.5,25.2" fill="none" stroke="rgba(34,195,172,.55)" strokeWidth="0.5" />
      </svg>
      <div className="absolute inset-0 overflow-hidden bg-[linear-gradient(170deg,#017C76,#3F738F_55%,#5A3B7E)]" style={{ clipPath: SECHSECK }}>
        {person.bild
          ? <img src={person.bild} alt={person.name} loading="lazy" className="h-full w-full object-cover" />
          : <Silhouette />}
      </div>
    </div>
  )
}

/** Weiche Trennstellen fuer lange Woerter der Flip-Card-Texte - die Browser-Silbentrennung trennt sie falsch
 *  („Produkti-onskapazitäten“). Die Texte in inhalte.ts bleiben unveraendert. */
const TRENNSTELLEN: [string, string][] = [
  ['Produktionskapazitäten', 'Produktions\u00ADkapazitäten'], ['internationaler', 'inter\u00ADnationaler'],
  ['Zusammenarbeit', 'Zusammen\u00ADarbeit'], ['Hauptgeschäftsführer', 'Haupt\u00ADgeschäfts\u00ADführer'],
  ['Clustermanager', 'Cluster\u00ADmanager'], ['Unternehmensperspektive', 'Unternehmens\u00ADperspektive'],
  ['systemkritischen', 'system\u00ADkritischen'], ['wissenschaftliche', 'wissen\u00ADschaftliche'],
  ['Digitalisierung', 'Digitali\u00ADsierung'], ['Kompetenzanforderungen', 'Kompetenz\u00ADanforderungen'],
  ['Transformation', 'Trans\u00ADformation'], ['Perspektiven', 'Perspek\u00ADtiven'],
  ['Industrieperspektive', 'Industrie\u00ADperspektive'], ['konzernweiten', 'konzern\u00ADweiten'],
  ['Institutsleiter', 'Instituts\u00ADleiter'], ['Forschungsdirektor', 'Forschungs\u00ADdirektor'],
  ['wehrtechnischen', 'wehr\u00ADtechnischen'], ['Verteidigungsf\u00E4higkeit', 'Verteidigungs\u00ADf\u00E4higkeit'],
  // neue Texte von Prof. Russack (19.10.2026)
  ['Unternehmensnetzwerke', 'Unternehmens\u00ADnetzwerke'], ['Rahmenbedingungen', 'Rahmen\u00ADbedingungen'],
  ['Vernetzungsplattform', 'Vernetzungs\u00ADplattform'], ['Organisationsforschung', 'Organisations\u00ADforschung'],
  ['Qualifizierungsbedarfe', 'Qualifizierungs\u00ADbedarfe'], ['Automatisierung', 'Automati\u00ADsierung'],
]
const trennen = (text = '') => TRENNSTELLEN.reduce((t, [wort, getrennt]) => t.split(wort).join(getrennt), text)

/** Kleines Wende-Symbol fuer die Hinweise auf Vorder- und Rueckseite */
function Wenden() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 6.5A5.5 5.5 0 0 0 3.3 4.6M2.5 9.5a5.5 5.5 0 0 0 10.2 1.9" />
      <path d="M3 1.8v3h3M13 14.2v-3h-3" />
    </svg>
  )
}

/** Hervorgehobene Karte über bzw. unter den Speakern (Grußwort, Moderation) */
function Rolle({ person, rolle, logo }: Readonly<{ person: SpeakerDaten; rolle: string; logo?: { datei: string; name: string } }>) {
  return (
    <figure className={`grid items-center gap-8 rounded-[32px] border border-white/10 bg-white/[.05] p-7 sm:grid-cols-[200px_1fr] sm:gap-10 sm:p-10 ${logo ? 'lg:grid-cols-[200px_1fr_auto]' : ''}`}>
      <Wabenportraet person={person} className="max-w-[200px]" />
      <figcaption className="text-center sm:text-left">
        <p className="inline-flex rounded-full bg-konf-hell px-3.5 py-1 font-display text-[12.5px] font-bold uppercase tracking-[0.24em] text-konf-nacht">
          {rolle}
        </p>
        <p className="mt-4 font-display text-[clamp(28px,3.2vw,40px)] font-bold leading-tight">{person.name}</p>
        <p className="mt-2 max-w-[52ch] text-[17px] leading-snug text-white/70">{person.funktion}</p>
      </figcaption>
      {/* nur das NRW-Wappen, ohne Ministeriumsnamen (Wunsch 06.10.2026) */}
      {logo && <img src={logo.datei} alt={logo.name} loading="lazy" className="mx-auto h-24 w-auto sm:col-span-2 lg:col-span-1 lg:mr-4 lg:h-28" />}
    </figure>
  )
}

/**
 * Speaker als Flip-Card: vorne Porträt, hinten der Vorstellungstext.
 * Dreht nur per Klick, Tippen oder Enter/Leertaste - nicht beim Darüberfahren (Wunsch 07.10.2026).
 * Bei „reduzierter Bewegung“ blendet die Rückseite nur über (CSS .flipkarte in index.css).
 */
function FlipKarte({ person }: Readonly<{ person: SpeakerDaten }>) {
  const [gedreht, setGedreht] = useState(false)
  return (
    <div className="flipkarte group relative h-full" data-gedreht={gedreht}>
      <div className="flipkarte-innen h-full">
        <figure className="flipkarte-vorn text-center" aria-hidden={gedreht}>
          <Wabenportraet person={person} />
          <figcaption className="mt-6">
            <p className="font-display text-[19px] font-bold leading-tight">{person.name}</p>
            <p className="mx-auto mt-1.5 max-w-[26ch] text-[14.5px] leading-snug text-white/65">{person.funktion}</p>
            {person.zugeschaltet && (
              <p className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-white/25 px-2.5 py-0.5 text-[12.5px] text-white/85">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-konf-hell" />{speakerAbschnitt.zugeschaltet}
              </p>
            )}
            <p className="mt-3 inline-flex items-center gap-1.5 font-display text-[11.5px] font-bold uppercase tracking-[0.2em] text-konf-hell transition-colors group-hover:text-white">
              <Wenden /> {speakerAbschnitt.mehr}
            </p>
          </figcaption>
        </figure>
        <div
          className="flipkarte-hinten flex min-w-0 flex-col rounded-[26px] border border-konf-hell/35 bg-[linear-gradient(165deg,#016E6A,#173A54_55%,#4A3470)] p-4 text-left shadow-[0_24px_60px_rgba(0,0,0,.35)] sm:p-6"
          aria-hidden={!gedreht}
        >
          {/* kleines Porträt erst ab Tablet - auf dem Handy reicht der Platz nur für den Text */}
          {person.bild && (
            <div aria-hidden="true" className="mb-4 hidden w-12 md:block">
              <div className="aspect-[866/1000] overflow-hidden" style={{ clipPath: SECHSECK }}>
                <img src={person.bild} alt="" loading="lazy" className="h-full w-full object-cover" />
              </div>
            </div>
          )}
          <p className="font-display text-[15px] font-bold leading-tight sm:text-[17px] lg:text-[18px]">{person.name}</p>
          <span aria-hidden="true" className="mt-3 block h-[3px] w-10 rounded-full bg-konf-hell" />
          <p className="mt-4 text-[13px] leading-[1.5] text-white/90 sm:text-[14.5px] lg:text-[15px]">{trennen(person.text)}</p>
          <p className="mt-auto inline-flex items-center gap-1.5 pt-4 font-display text-[11.5px] font-bold uppercase tracking-[0.2em] text-konf-hell">
            <Wenden /> {speakerAbschnitt.zurueck}
          </p>
        </div>
      </div>
      {/* eine Schaltfläche über der ganzen Karte - für Tippen und Tastatur */}
      <button
        type="button"
        className="absolute inset-0 cursor-pointer rounded-[26px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-konf-hell"
        aria-pressed={gedreht}
        aria-label={`${person.name}: ${gedreht ? speakerAbschnitt.zurueck : speakerAbschnitt.mehr}`}
        onClick={() => setGedreht(g => !g)}
      />
    </div>
  )
}

// Bildrechte, die genannt werden müssen (Feld copyright in inhalte.ts)
const fotonachweis = [grusswort, ...speaker, moderation].filter(s => s.copyright).map(s => `${s.name} © ${s.copyright}`)

export default function Speaker() {
  return (
    <section id="speaker" className="verlauf-nacht relative isolate overflow-hidden py-20 text-white sm:py-28">
      <Wabennetz className="h-[40%]" staerke={0.2} />
      <div className="huelle">
        <Etikett aufDunkel>{speakerAbschnitt.etikett}</Etikett>
        <h2 className="mt-10 max-w-[18ch] text-[clamp(32px,4.2vw,54px)]">{speakerAbschnitt.titel}</h2>
        <Einblenden className="mt-14">
          <Rolle person={grusswort} rolle={speakerAbschnitt.grusswort} logo={grusswortLogo} />
        </Einblenden>
        {/* sieben Karten: 2 / 3 / 4 je Reihe, eine angefangene letzte Reihe steht mittig */}
        <ul className="mt-16 flex flex-wrap justify-center gap-x-5 gap-y-12">
          {speaker.map((person, i) => (
            <li key={person.name} className="w-[calc((100%-1.25rem)/2)] md:w-[calc((100%-2.5rem)/3)] lg:w-[calc((100%-3.75rem)/4)]">
              <Einblenden verzoegerung={i * 80} className="h-full">
                <FlipKarte person={person} />
              </Einblenden>
            </li>
          ))}
        </ul>
        <Einblenden className="mt-16">
          <Rolle person={moderation} rolle={speakerAbschnitt.moderation} />
        </Einblenden>
        {fotonachweis.length > 0 && (
          <p className="mt-8 text-[12.5px] text-white/45">{speakerAbschnitt.fotonachweis}: {fotonachweis.join(' · ')}</p>
        )}
      </div>
    </section>
  )
}
